import React, { useMemo } from "react";
import styled from "styled-components";
import { useDebounce } from "use-debounce";
import {
  Popover,
  PopoverAnchor,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
} from "@chakra-ui/react";
import SlotGameCardNew from "./SlotGameCardNew";
import { SlotGrid } from "./SlotsSection";
import WrewardsDefaultInput from "components/wrewards-ui-kit/Input/DefaultInput";
import SearchSvg from "/public/images/icons/search.svg";
import { WGamesItemCard } from "components/new-home-page/WGamesComponent";
import useSlotGames from "hooks/slots/useSlotGames";
import useGetWGames from "hooks/games/useGetWGames";

const SearchGames = () => {
  const ref = React.useRef(null);
  const [search, setSearch] = React.useState("");
  const [isOpen, setOpen] = React.useState(false);
  const [debouncedSearch] = useDebounce(search, 1000);
  const { WGames } = useGetWGames();

  const { data, isFetching, isFetched } = useSlotGames({
    take: 4,
    skip: 0,
    search: debouncedSearch,
    enabled: debouncedSearch !== "",
  });

  const originItems = useMemo(() => {
    return WGames.filter(
      (game) => !!debouncedSearch && !game.disabled && game.title.includes(debouncedSearch)
    );
  }, [WGames, debouncedSearch]);

  React.useEffect(() => {
    const isSlotFound = data && data?.data?.length > 0;
    const isWGameFound = originItems.length > 0;
    if (isSlotFound || isWGameFound) {
      setOpen(true);
    }
  }, [data, originItems]);

  return (
    <>
      <Popover
        isOpen={isOpen && debouncedSearch !== ""}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
        }}
        placement="bottom-start"
        closeOnBlur={false}
        isLazy={false}
        defaultIsOpen={false}
        closeOnEsc
      >
        {() => {
          return (
            <>
              <PopoverAnchor>
                <PopoverTrigger>
                  <WrewardsDefaultInput
                    props={{
                      placeholder: "Search",
                      style: { padding: "0 0 0 24px " },
                      value: search,
                      onChange: (e) => setSearch(e.target.value),
                    }}
                  >
                    <div style={{ position: "absolute", display: "flex", color: "inherit" }}>
                      <SearchSvg width={16} height={16} color={"#c8d7f58f"} />
                    </div>
                    <div
                      ref={ref}
                      style={{
                        position: "absolute",
                        left: 0,
                        bottom: "-1rem",
                        right: 0,
                        width: "100%",
                        zIndex: 1000,
                      }}
                    />
                  </WrewardsDefaultInput>
                </PopoverTrigger>
              </PopoverAnchor>

              <Portal containerRef={ref}>
                <PopoverContent
                  as={SearchResultContent}
                  className="content"
                  width={"100%"}
                  background={"transparent"}
                  minW={"inherit"}
                >
                  <PopoverCloseButton as={SearchCloseButton} />
                  <SearchSlotGrid>
                    {isFetching ? (
                      <LoaderWrapper>
                        <Spinner />
                      </LoaderWrapper>
                    ) : (
                      <>
                        {data?.data?.map((slot) => {
                          return (
                            <SlotGameCardNew
                              className="card"
                              key={slot.code}
                              code={slot.code}
                              codeSlug={slot.codeSlug}
                              imageCode={slot.imageCode}
                              imageBgUrl={slot.imageBgUrl}
                              imageIconUrl={slot.imageIconUrl}
                              imageTextUrl={slot.imageTextUrl}
                              name={slot.name}
                              provider={slot.provider}
                              isFavorite={slot.isFavorite}
                              eager
                            />
                          );
                        })}

                        {originItems.map(({ link, imageSrc, title, disabled }) => {
                          return (
                            <WGamesItemCard
                              key={title}
                              link={link}
                              imageSrc={imageSrc}
                              title={title}
                              disabled={disabled}
                            />
                          );
                        })}
                      </>
                    )}
                  </SearchSlotGrid>

                  {data && data?.data?.length <= 0 && isFetched && originItems.length <= 0 && (
                    <span className="empty-message">No Result Found</span>
                  )}
                </PopoverContent>
              </Portal>
            </>
          );
        }}
      </Popover>
    </>
  );
};

const LoaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const SearchCloseButton = styled.button`
  background: #e9f4ff0d;
  border: none;
  cursor: pointer;
  color: #fff;
`;

const SearchResultContent = styled.section`
  --popper-arrow-bg: #1e213a;
  font-family: var(--font-family-golos);
  &.content {
    padding: 24px;
    width: 100%;
    background-color: #101622;
    border: 1px solid #1e213a;
  }

  .empty-message {
    text-align: center;
    text-transform: uppercase;
    padding: 16px;
    margin-bottom: 32px;
  }
`;

const SearchSlotGrid = styled(SlotGrid)`
  width: 100%;
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 16px;
  position: relative;
`;

export default SearchGames;
